import { FieldValues } from 'react-hook-form/dist/types/fields';
import { isNaN } from 'lodash';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import {
  Template,
  Validator,
  Domanda as DomandaSurvey,
} from '../api/generated/dob';

interface Domanda extends Omit<DomandaSurvey, 'strutturaInternaDomande'> {
  answersFilled: Array<number>;
  strutturaInternaDomande: {
    domande: Array<Domanda>;
  };
}

const VALIDATE_KO_AND = 'validate_ko_and';
const VALIDATE_KO_AND_IS_FALSE = 'validate_ko_and_if';
function filterValidator(
  validator: Validator,
  questions: Array<Domanda>
): boolean {
  const { idRispostaDipendenza, idDomandaDipendenza } = validator;
  if (isNaN(idRispostaDipendenza)) return false;
  return questions?.some((question) => {
    if ((question.strutturaInternaDomande?.domande?.length ?? 0) !== 0) {
      return filterValidator(
        validator,
        question.strutturaInternaDomande?.domande
      );
    }

    if (
      validator.tipoDipendenza === VALIDATE_KO_AND ||
      validator.tipoDipendenza === 'validate_ko'
    )
      return (
        question.idDomanda === idDomandaDipendenza &&
        question.answersFilled.includes(idRispostaDipendenza || -1)
      );
    if (validator.tipoDipendenza === VALIDATE_KO_AND_IS_FALSE) {
      return (
        question.idDomanda === idDomandaDipendenza &&
        !question.answersFilled.includes(idRispostaDipendenza || -1)
      );
    }
    return false;
  });
}

function getInvalidQuestions(
  questions: Array<Domanda>,
  question: Domanda
): Array<Domanda> {
  const internalQuestions = question.strutturaInternaDomande.domande;
  if (internalQuestions.length !== 0) {
    return internalQuestions.filter((questionLocal) =>
      isInvalid(questions, questionLocal)
    );
  }

  const validators = question.validators?.filter((validator) =>
    question.answersFilled.includes(validator.idRisposta || -1)
  );

  if (!Array.isArray(validators) || validators.length === 0) {
    return [];
  }

  const validatorsGroups = Array.from(
    new Set(validators.map((v) => v.idGroup ?? -1))
  );
  let invalidQuestions: Array<Domanda> = [];

  validatorsGroups.forEach((g) => {
    const validatorsForGroup = validators.filter(
      (v) => (v.idGroup ?? -1) === g
    );
    const validatorsInError: Array<Validator> = validatorsForGroup.filter((v) =>
      filterValidator(v, questions)
    );
    if (validatorsInError.length === 0) return;

    if (
      validatorsInError?.some(
        (v) =>
          v.tipoDipendenza === VALIDATE_KO_AND ||
          v.tipoDipendenza === VALIDATE_KO_AND_IS_FALSE
      ) &&
      validatorsForGroup.length !== validatorsInError.length
    ) {
      return;
    }

    let invalidQuestionsForGroup: Domanda[] = [];
    validatorsInError.forEach((v) => {
      let retQ: Domanda | undefined;
      questions.forEach((_q) => {
        if ((_q.strutturaInternaDomande?.domande?.length ?? 0) !== 0) {
          const q = _q.strutturaInternaDomande.domande.find(
            (qi) => v.idDomandaDipendenza === qi.idDomanda
          );
          if (q) {
            retQ = q;
            return;
          }
        }
        if (v.idDomandaDipendenza === _q.idDomanda) retQ = _q;
      });
      if (retQ) invalidQuestionsForGroup = [...invalidQuestionsForGroup, retQ];
    });

    invalidQuestions = [...invalidQuestions, ...invalidQuestionsForGroup];
  });
  return invalidQuestions;
}

function isInvalid(questions: Array<Domanda>, question: Domanda): boolean {
  return getInvalidQuestions(questions, question).length !== 0;
}

function checkMandatory(
  question: Domanda,
  parentIsMandatory: boolean = false,
  allQuestions: Domanda[] = []
): Array<Domanda> {
  let invalid: Array<Domanda> = [];

  const alwaysMandatoryIds = [165, 168];
  const forceMandatory = alwaysMandatoryIds.includes(question.idDomanda || 0);

  const conditionalMandatoryIds = [255, 290, 293, 296, 299];
  let conditionalMandatory = false;
  let isConditionalQuestion = conditionalMandatoryIds.includes(
    question.idDomanda || 0
  );

  const subCheckboxMapping: Record<
    number,
    { parentId: number; requiredValue: number }
  > = {
    284: { parentId: 285, requiredValue: 330 },
    266: { parentId: 267, requiredValue: 276 },
    170: { parentId: 171, requiredValue: 200 },
  };

  const isSubCheckbox = question.idDomanda
    ? subCheckboxMapping[question.idDomanda] !== undefined
    : false;

  if (isConditionalQuestion && allQuestions.length > 0) {
    if (question.idDomanda === 255) {
      const question166 = allQuestions.find(
        (q: Domanda) => q.idDomanda === 166
      );
      const isLungo = question166?.answersFilled?.includes(193);
      conditionalMandatory =
        !isLungo && (question166?.answersFilled?.length || 0) > 0;
    } else {
      const question285 = allQuestions.find(
        (q: Domanda) => q.idDomanda === 285
      );
      conditionalMandatory = question285?.answersFilled?.includes(330) || false;
    }
  }

  if (isSubCheckbox && question.idDomanda && allQuestions.length > 0) {
    const config = subCheckboxMapping[question.idDomanda];
    const parentQuestion = allQuestions.find(
      (q: Domanda) => q.idDomanda === config.parentId
    );
    conditionalMandatory =
      parentQuestion?.answersFilled?.includes(config.requiredValue) || false;
  }

  let isMandatory;
  if (isConditionalQuestion || isSubCheckbox) {
    isMandatory = conditionalMandatory;
  } else {
    isMandatory =
      parentIsMandatory ||
      question.flagMandatory ||
      question.testoDomanda?.includes('*') ||
      forceMandatory ||
      false;
  }

  const hasChildren =
    question.strutturaInternaDomande?.domande &&
    question.strutturaInternaDomande.domande.length > 0;

  const shouldValidateEvenWithChildren = isSubCheckbox;

  if (
    isMandatory &&
    (shouldValidateEvenWithChildren || !hasChildren) &&
    (!question.answersFilled || question.answersFilled.length === 0)
  ) {
    invalid.push(question);
  }

  if (hasChildren) {
    question.strutturaInternaDomande.domande.forEach((q) => {
      invalid = [...invalid, ...checkMandatory(q, isMandatory, allQuestions)];
    });
  }

  return invalid;
}

function linkAnswers(question: DomandaSurvey, values: FieldValues): Domanda {
  const idDomanda = (question.idDomanda || 0).toString();

  const inputType =
    question.strutturaInternaDomande?.listaDominiRisposte?.at(0)?.descrizione;
  return {
    ...question,
    strutturaInternaDomande: {
      domande:
        question.strutturaInternaDomande?.domande?.map((questionLocal) =>
          linkAnswers({ ...questionLocal, inputType }, values)
        ) || [],
    },
    answersFilled: Object.keys(values)
      .filter((key) => {
        return key.split('-')[1] === idDomanda;
      })
      .reduce((results: Array<number>, key) => {
        if (values[key] != null) {
          const value = values[key];
          if (typeof value === 'string') {
            if (value.trim() === '') {
              return results;
            }
            value.split(',').forEach((v: string) => {
              const trimmed = v.trim();
              if (trimmed !== '' && !isNaN(Number(trimmed))) {
                results.push(Number(trimmed));
              }
            });
          } else if (typeof value === 'number') {
            results.push(value);
          }
        }
        return results;
      }, []),
  };
}

interface builderResolverSchema {
  template: Template;
  default: {
    errorMessage?: string | undefined;
  };
}

function buildResolver(schema: builderResolverSchema): any {
  return function resolver(values) {
    const {
      template,
      default: { errorMessage = 'Answer is not valid' },
    } = schema;
    const errors: FieldErrors = {};

    try {
      let allLinkedQuestions: Domanda[] = [];

      // Funzione helper per appiattire le domande ricorsivamente
      const flattenQuestions = (questions: Domanda[]): Domanda[] => {
        return questions.reduce((flat: Domanda[], q) => {
          flat.push(q);
          if (q.strutturaInternaDomande?.domande?.length) {
            flat.push(...flattenQuestions(q.strutturaInternaDomande.domande));
          }
          return flat;
        }, []);
      };

      template?.sezioni?.forEach((s) => {
        if (Array.isArray(s.listaDomande)) {
          const linked =
            s.listaDomande?.map((domanda) => linkAnswers(domanda, values)) ??
            [];
          allLinkedQuestions = [
            ...allLinkedQuestions,
            ...flattenQuestions(linked),
          ];
        }
      });

      template?.sezioni?.forEach((s) => {
        if (Array.isArray(s.listaDomande)) {
          const { listaDomande } = s;
          const linked =
            listaDomande?.map((domanda) => linkAnswers(domanda, values)) ?? [];

          linked.forEach((domanda) => {
            if (isNaN(domanda.idDomanda)) return;

            const excludedIds = [263, 264, 265, 125, 126, 127, 120];
            const idsToCheck = [
              domanda.idDomanda || -1,
              ...(domanda.strutturaInternaDomande?.domande?.map(
                (d) => d.idDomanda || -1
              ) || []),
            ];
            const shouldExclude = idsToCheck?.some((id) =>
              excludedIds.includes(id)
            );

            if (shouldExclude) return;

            const invalidQuestions = getInvalidQuestions(
              allLinkedQuestions,
              domanda
            );
            const mandatoryQuestions = checkMandatory(
              domanda,
              false,
              allLinkedQuestions
            );

            mandatoryQuestions.forEach((question) => {
              const id = question?.idDomanda?.toString() || '';
              errors[`survey-${id}`] = {
                type: 'required',
                message: errorMessage,
              };
            });

            invalidQuestions.forEach((question) => {
              const id = question?.idDomanda?.toString() || '';
              if (!errors[`survey-${id}`]) {
                errors[`survey-${id}`] = {
                  type: 'validation',
                  message: errorMessage,
                };
              }
            });
          });
        }
      });

      return {
        values,
        errors,
      };
    } catch (e) {
      console.log('Errore resolver' + e);
      return {
        values,
        errors,
      };
    }
  };
}

export default buildResolver;
